import { getHostUrl } from "@/composables/store";
import { useUser } from "@/stores/user";

// Number of companies the signed-in user has saved. The navigation loads it
// during server rendering so the badge is present on first paint, while save
// and unsave actions update the same shared state immediately on the client.
export const SAVED_COMPANY_COUNT_KEY = "ucc-saved-company-count";

function resolveToken(): string {
	const value = useUser().token as unknown;
	if (typeof value === "string") return value;
	if (value && typeof value === "object" && "value" in value) {
		const nested = (value as { value?: unknown }).value;
		return typeof nested === "string" ? nested : "";
	}
	return "";
}

export function useSavedCompanyCount() {
	const count = useState<number>(SAVED_COMPANY_COUNT_KEY, () => 0);
	const loaded = useState<boolean>(`${SAVED_COMPANY_COUNT_KEY}-loaded`, () => false);

	async function loadSavedCompanyCount(): Promise<number> {
		const token = resolveToken();
		if (!token) {
			count.value = 0;
			loaded.value = true;
			return 0;
		}
		try {
			const response = await fetch(`${getHostUrl()}/ucc/saved-companies?limit=1&offset=0`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			if (!response.ok) throw new Error(`Saved-company count request failed (${response.status})`);
			const payload = await response.json();
			count.value = Math.max(0, Number(payload?.total || 0));
		} catch (error) {
			console.error("Unable to load saved-company count", error);
			count.value = 0;
		} finally {
			loaded.value = true;
		}
		return count.value;
	}

	return { savedCompanyCount: count, savedCompanyCountLoaded: loaded, loadSavedCompanyCount };
}
